/**
 * Input
 *
 * Shared text input primitive for forms, filters, and command fields.
 *
 * Primary responsibilities:
 * - Provide token-based focus, error, and disabled states
 * - Wire helper text with accessible descriptions
 *
 * Dependencies:
 * - lib/utils
 */

'use client'

import * as React from 'react'

import { cn } from '@/lib/utils'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  error?: boolean
  description?: string
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      error,
      description,
      id,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
      ...props
    },
    ref
  ) => {
    const generatedDescriptionId = React.useId()
    const descriptionId = description
      ? id
        ? `${id}-description`
        : generatedDescriptionId
      : undefined
    const describedBy =
      [ariaDescribedBy, descriptionId]
        .filter((value): value is string => Boolean(value))
        .join(' ') || undefined

    const inputClasses = cn(
      'border-input bg-background placeholder:text-muted-foreground focus-visible:ring-primary/40 hover:border-foreground/20 flex h-10 w-full rounded-md border px-3 py-2 text-sm shadow-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50 motion-safe:transition-[box-shadow,border-color] motion-safe:duration-150 motion-reduce:transition-none',
      error && 'border-destructive focus-visible:ring-destructive',
      className
    )

    const input = (
      <input
        type={type}
        id={id}
        className={inputClasses}
        ref={ref}
        aria-invalid={error ? 'true' : ariaInvalid}
        aria-describedby={describedBy}
        {...props}
      />
    )

    if (!description) {
      return input
    }

    return (
      <>
        {input}
        <p
          id={descriptionId}
          className={cn('text-muted-foreground mt-1 text-xs', error && 'text-destructive')}
        >
          {description}
        </p>
      </>
    )
  }
)
Input.displayName = 'Input'

export { Input }
